import React, { useState } from "react";
import { toast } from "sonner";
import { format } from "date-fns";
import api from "@/lib/api";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { CalendarBlank, UploadSimple, FileText } from "@phosphor-icons/react";

const CRED_TYPES_BY_ROLE = {
  coach: ["CPR/First Aid", "SafeSport", "Background Check", "NFHS Coaching"],
  trainer: ["State License", "BOC Certification", "CPR/First Aid"],
  referee: ["USSF Certification", "NFHS Officiating", "Background Check"],
  volunteer: ["Background Check", "SafeSport"],
};

export default function UploadCredentialDialog({ open, onOpenChange, staff, onCreated }) {
  const [cred_type, setCredType] = useState("");
  const [issuer, setIssuer] = useState("");
  const [issue_date, setIssueDate] = useState(null);
  const [expiration_date, setExpirationDate] = useState(null);
  const [notes, setNotes] = useState("");
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);

  const suggestions = CRED_TYPES_BY_ROLE[staff?.role] || [];

  const reset = () => {
    setCredType(""); setIssuer(""); setIssueDate(null); setExpirationDate(null); setNotes(""); setFile(null);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!cred_type.trim() || !expiration_date) {
      toast.error("Credential type and expiration date are required");
      return;
    }
    setLoading(true);
    try {
      const fd = new FormData();
      fd.append("staff_id", staff.id);
      fd.append("cred_type", cred_type);
      fd.append("expiration_date", format(expiration_date, "yyyy-MM-dd"));
      if (issuer) fd.append("issuer", issuer);
      if (issue_date) fd.append("issue_date", format(issue_date, "yyyy-MM-dd"));
      if (notes) fd.append("notes", notes);
      if (file) fd.append("file", file);
      const { data } = await api.post("/credentials", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Credential uploaded successfully");
      reset();
      onCreated?.(data);
      onOpenChange(false);
    } catch (err) {
      toast.error("Failed to upload credential");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="bg-slate-900 border-white/10 text-slate-50 max-w-lg max-h-[90vh] overflow-y-auto"
        data-testid="upload-credential-dialog"
      >
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">Upload Credential</DialogTitle>
          <DialogDescription className="text-slate-400">
            {staff ? `Add certification for ${staff.name}` : "Add certification"}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={submit} className="space-y-4">
          <div>
            <Label className="font-label text-slate-400">CREDENTIAL TYPE *</Label>
            <Input
              data-testid="cred-type-input"
              value={cred_type}
              onChange={(e) => setCredType(e.target.value)}
              placeholder="e.g. SafeSport, CPR/First Aid"
              className="bg-slate-950 border-white/10 focus:border-orange-500 mt-1"
              required
              list="cred-suggestions"
            />
            <datalist id="cred-suggestions">
              {suggestions.map((s) => <option key={s} value={s} />)}
            </datalist>
            {suggestions.length > 0 && (
              <div className="flex flex-wrap gap-1.5 mt-2">
                {suggestions.map((s) => (
                  <button
                    key={s}
                    type="button"
                    onClick={() => setCredType(s)}
                    className="text-xs px-2 py-1 rounded-full border border-white/10 text-slate-400 hover:text-orange-400 hover:border-orange-500/40 transition-colors"
                  >
                    {s}
                  </button>
                ))}
              </div>
            )}
          </div>

          <div>
            <Label className="font-label text-slate-400">ISSUING BODY</Label>
            <Input
              value={issuer}
              onChange={(e) => setIssuer(e.target.value)}
              placeholder="American Red Cross, NFHS, USSF…"
              className="bg-slate-950 border-white/10 focus:border-orange-500 mt-1"
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="font-label text-slate-400">ISSUE DATE</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    type="button"
                    variant="outline"
                    className="w-full mt-1 justify-start bg-slate-950 border-white/10 text-slate-100 hover:bg-slate-900 hover:text-slate-50"
                  >
                    <CalendarBlank size={16} className="mr-2 text-slate-400" />
                    {issue_date ? format(issue_date, "MMM d, yyyy") : "Pick date"}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0 bg-slate-900 border-white/10">
                  <Calendar mode="single" selected={issue_date} onSelect={setIssueDate} />
                </PopoverContent>
              </Popover>
            </div>
            <div>
              <Label className="font-label text-slate-400">EXPIRATION *</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    type="button"
                    variant="outline"
                    data-testid="cred-expiration-picker"
                    className="w-full mt-1 justify-start bg-slate-950 border-white/10 text-slate-100 hover:bg-slate-900 hover:text-slate-50"
                  >
                    <CalendarBlank size={16} className="mr-2 text-slate-400" />
                    {expiration_date ? format(expiration_date, "MMM d, yyyy") : "Pick date"}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0 bg-slate-900 border-white/10">
                  <Calendar mode="single" selected={expiration_date} onSelect={setExpirationDate} />
                </PopoverContent>
              </Popover>
            </div>
          </div>

          <div>
            <Label className="font-label text-slate-400">NOTES</Label>
            <Textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Optional notes"
              className="bg-slate-950 border-white/10 focus:border-orange-500 mt-1 min-h-[70px]"
            />
          </div>

          <div>
            <Label className="font-label text-slate-400">CERTIFICATE FILE (PDF/IMAGE)</Label>
            <label
              htmlFor="cred-file-input"
              className="mt-1 flex items-center gap-3 rounded-lg border-2 border-dashed border-white/10 bg-slate-950 p-4 cursor-pointer hover:border-orange-500/40 transition-colors"
            >
              {file ? <FileText size={22} className="text-orange-400" weight="bold" /> : <UploadSimple size={22} className="text-slate-400" />}
              <div className="text-sm">
                <div className="text-slate-100 font-semibold">
                  {file ? file.name : "Drop file or click to upload"}
                </div>
                <div className="text-slate-500 text-xs">PDF, PNG, JPG accepted</div>
              </div>
            </label>
            <input
              id="cred-file-input"
              data-testid="cred-file-input"
              type="file"
              accept=".pdf,image/*"
              className="hidden"
              onChange={(e) => setFile(e.target.files?.[0] || null)}
            />
          </div>

          <DialogFooter>
            <Button type="button" variant="ghost" onClick={() => onOpenChange(false)} className="text-slate-300">
              Cancel
            </Button>
            <Button
              data-testid="submit-credential-button"
              type="submit"
              disabled={loading}
              className="bg-orange-500 text-white hover:bg-orange-400 font-semibold"
            >
              {loading ? "Uploading…" : "Save Credential"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
